// A2 R3 #15 + #16 — League Format History.
//
// Walks seasons oldest→newest and emits two chronological streams:
//
//   1. formatChanges[]: year-over-year diffs in roster slots, scoring (PPR),
//      median scoring, playoff team count, total team count, taxi slots (SF).
//      Skipped on the first season (no prior to diff against). The first
//      season also gets an explicit "baseline" entry so the timeline isn't
//      missing context for "what was the original format".
//
//   2. seasonLabels[]: one entry per season with the keeper/draft label
//      (Startup, Rookie Draft, Keeper, Redraft) plus, where the per-pick
//      data exists (Sleeper), the list of who was kept.
//
// Platform asymmetry:
//   - Sleeper: full settings (roster_positions, scoring_settings, taxi_slots,
//     league_average_match, playoff_week_start, playoff_teams, max_keepers,
//     total_rosters) and per-pick is_keeper.
//   - Yahoo: surfaced via the league_settings proxy after R3 expansion —
//     roster_positions, stat_modifiers, playoff_start_week, num_playoff_teams,
//     uses_median_score, uses_keeper, num_teams. No per-pick keeper flag, so
//     keeper labels are detected but kept-player lists are omitted.
//
// Returns { formatChanges, seasonLabels, hasContent } where hasContent is
// false when there's nothing meaningful to render (1 season, no diffs).

const SUPER_FLEX_SLOTS = new Set(["SUPER_FLEX", "SUPERFLEX", "Q/W/R/T", "QB/WR/RB/TE"]);
const FLEX_SLOTS = new Set(["FLEX", "WRT", "W/R/T", "RB/WR/TE"]);

export function computeLeagueEvolution(seasons, platform, statsDrafts = []) {
  if (!Array.isArray(seasons) || seasons.length === 0) {
    return { formatChanges: [], seasonLabels: [], hasContent: false };
  }
  const sorted = [...seasons]
    .filter(s => s && !s.seasonError && s.season != null)
    .sort((a, b) => String(a.season).localeCompare(String(b.season)));
  if (!sorted.length) return { formatChanges: [], seasonLabels: [], hasContent: false };

  // Adapter-produced drafts indexed by year for keeper-detail lookups.
  // stats.drafts[].picks have canonical owner names already - we'd lose them
  // walking the raw season.drafts picks (which only carry roster_id).
  const draftsByYear = {};
  for (const d of statsDrafts || []) {
    if (d?.year) draftsByYear[String(d.year)] = d;
  }

  const summaries = sorted.map(s => summarizeSeason(s, platform));
  const formatChanges = [];

  // Baseline entry for the oldest season so the timeline starts with
  // "here's what the league looked like" instead of jumping to year-2 diffs.
  formatChanges.push({
    year: summaries[0].year,
    isBaseline: true,
    changes: baselineLines(summaries[0]),
  });

  for (let i = 1; i < summaries.length; i++) {
    const prev = summaries[i - 1];
    const curr = summaries[i];
    const changes = diffSeasons(prev, curr);
    if (changes.length) {
      formatChanges.push({ year: curr.year, isBaseline: false, changes });
    }
  }

  const seasonLabels = sorted.map((season, i) => buildSeasonLabel(season, summaries[i], platform, draftsByYear[summaries[i].year]));

  // hasContent: only render the section if there's at least one diff entry
  // (beyond the baseline) OR at least one non-trivial keeper/draft label.
  const hasDiffs = formatChanges.length > 1;
  const hasLabels = seasonLabels.some(l => l && l.label && l.label !== "Redraft");
  return {
    formatChanges,
    seasonLabels,
    hasContent: hasDiffs || hasLabels,
  };
}

// ---------- summarize ----------

// Per-season snapshot — small flat object the diff/baseline helpers walk.
// Keeps the diff logic platform-agnostic.
function summarizeSeason(season, platform) {
  const year = String(season.season);
  const scoring = normalizeScoring(season, platform);
  const nonCoreScoring = collectNonCoreScoring(season, platform);
  if (platform === "sleeper") {
    const s = season.settings || {};
    const slots = countSlots(season.roster_positions || []);
    return {
      year,
      teamCount: season.total_rosters || null,
      rosterSlots: slots,
      scoring,
      nonCoreScoring,
      medianOn: Number(s.league_average_match) === 1,
      playoffTeams: numberOr(s.playoff_teams, null),
      playoffStartWeek: numberOr(s.playoff_week_start, null),
      taxiSlots: numberOr(s.taxi_slots, null),
      maxKeepers: numberOr(s.max_keepers, null),
    };
  }
  // Yahoo
  const s = season.settings || {};
  const slots = countSlots(s.roster_positions || []);
  return {
    year,
    teamCount: s.num_teams ?? season.num_teams ?? null,
    rosterSlots: slots,
    scoring,
    nonCoreScoring,
    medianOn: String(s.uses_median_score ?? "0") === "1",
    playoffTeams: s.num_playoff_teams ?? null,
    playoffStartWeek: s.playoff_start_week ?? null,
    taxiSlots: null, // Yahoo doesn't expose taxi
    maxKeepers: null,
    keeperLeague: String(s.uses_keeper ?? "0") === "1",
  };
}

// Two platform shapes converge here:
//   Sleeper: roster_positions is a flat array of repeated strings,
//            e.g. ["QB","RB","RB","WR","WR","WR","TE","FLEX","K","DEF",
//                  "BN","BN","BN","BN","BN","BN"]. Increment +1 per entry.
//   Yahoo:   roster_positions is [{roster_position: {position, count, ...}}]
//            - each position listed ONCE with its slot count. Read count
//            keyed by position. Verified against a Smithtown raw response
//            (RB count 2, WR count 3, BN count 6).
function countSlots(slotList) {
  const counts = {};
  for (const slot of slotList) {
    if (typeof slot === "string") {
      counts[slot] = (counts[slot] || 0) + 1;
      continue;
    }
    // Yahoo's count-bearing shape - try the wrapped form first, then the
    // already-unwrapped form as a defensive fallback.
    const inner = slot?.roster_position || slot;
    const pos = inner?.position;
    if (!pos) continue;
    const count = Number(inner?.count);
    counts[pos] = (counts[pos] || 0) + (Number.isFinite(count) && count > 0 ? count : 1);
  }
  return counts;
}

function numberOr(v, fallback) {
  if (v == null) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

// ---------- baseline ----------

function baselineLines(snap) {
  const lines = [];
  if (snap.teamCount) lines.push(`${snap.teamCount}-team league`);
  const startingLineup = describeStartingLineup(snap.rosterSlots);
  if (startingLineup) lines.push(`Starting lineup: ${startingLineup}`);
  const benchTaxi = describeBenchTaxi(snap.rosterSlots, snap.taxiSlots);
  if (benchTaxi) lines.push(benchTaxi);
  const scoringLine = describeScoringDetail(snap.scoring);
  if (scoringLine) lines.push(`Scoring: ${scoringLine}`);
  const bonusLine = describeBonuses(snap.scoring?.bonuses);
  if (bonusLine) lines.push(`Bonuses: ${bonusLine}`);
  if (snap.medianOn) lines.push("Median scoring on");
  if (snap.playoffTeams) lines.push(`${snap.playoffTeams} playoff teams`);
  if (snap.playoffStartWeek) lines.push(`Playoffs begin week ${snap.playoffStartWeek}`);
  return lines;
}

function describeStartingLineup(slots) {
  // Filter out bench/IR/taxi slots from the starting lineup display.
  const skip = new Set(["BN", "IR", "TAXI", "RES"]);
  const parts = [];
  for (const [slot, count] of Object.entries(slots)) {
    if (skip.has(slot)) continue;
    const display = displaySlotName(slot);
    parts.push(count > 1 ? `${count}${display}` : display);
  }
  return parts.join(" / ");
}

// Display normalization for slot keys. Sleeper uses SUPER_FLEX, FLEX_TE,
// etc.; render as SUPERFLEX, FLEX-TE so the timeline reads cleanly.
function displaySlotName(slot) {
  if (SUPER_FLEX_SLOTS.has(slot)) return "SUPERFLEX";
  if (FLEX_SLOTS.has(slot)) return "FLEX";
  return String(slot).replace(/_/g, "-");
}

function describeBenchTaxi(slots, taxiSlots) {
  const parts = [];
  if (slots.BN) parts.push(`${slots.BN} bench`);
  if (slots.IR) parts.push(`${slots.IR} IR`);
  const taxi = slots.TAXI || taxiSlots;
  if (taxi) parts.push(`${taxi} taxi`);
  return parts.length ? parts.join(" / ") : null;
}

// ---------- scoring normalization (SCORING_DETAIL_v1) ----------
//
// Both platforms map into one model so display + diff stay platform-agnostic:
//   { passYd, passTd, passInt, rushYd, rushTd, rec, recYd, recTd, fumLost,
//     bonuses: [{stat, threshold, points}] }
// rec is the PPR value (0 / 0.5 / 1 / custom).
//
// Sleeper:   season.scoring_settings - flat { key: value } object.
// Yahoo:     season.settings.stat_modifiers.stats - wrapper object around an
//            array of { stat: { stat_id, value, bonuses? } }. Confirmed via
//            the raw league_settings response; stat_id 11 = receptions.

const YAHOO_STAT_ID = {
  4: "passYd", 5: "passTd", 6: "passInt",
  9: "rushYd", 10: "rushTd",
  11: "rec", 12: "recYd", 13: "recTd",
  18: "fumLost",
};
// stat_ids whose bonuses (per-yard thresholds) carry the per-yd headline.
// Yahoo nests bonuses under each per-yard stat entry as
// `bonuses: [{ bonus: { target, points } }]`.
const YAHOO_BONUS_ON_STAT = { 4: "passYd", 9: "rushYd", 12: "recYd" };

const SLEEPER_SCORING_KEY = {
  pass_yd: "passYd", pass_td: "passTd", pass_int: "passInt",
  rush_yd: "rushYd", rush_td: "rushTd",
  rec: "rec", rec_yd: "recYd", rec_td: "recTd",
  fum_lost: "fumLost",
};

const EMPTY_SCORING = {
  passYd: null, passTd: null, passInt: null,
  rushYd: null, rushTd: null,
  rec: null, recYd: null, recTd: null,
  fumLost: null,
  bonuses: [],
};

export function normalizeScoring(season, platform) {
  if (platform === "sleeper") return normalizeSleeperScoring(season?.scoring_settings || {});
  if (platform === "yahoo") return normalizeYahooScoring(season?.settings?.stat_modifiers?.stats || []);
  return { ...EMPTY_SCORING };
}

function normalizeSleeperScoring(scoring) {
  const out = { ...EMPTY_SCORING, bonuses: [] };
  for (const [key, cat] of Object.entries(SLEEPER_SCORING_KEY)) {
    out[cat] = numberOr(scoring[key], null);
  }
  return out;
}

function normalizeYahooScoring(stats) {
  const out = { ...EMPTY_SCORING, bonuses: [] };
  for (const entry of stats) {
    const stat = entry?.stat;
    if (!stat) continue;
    const id = Number(stat.stat_id);
    const cat = YAHOO_STAT_ID[id];
    if (cat) out[cat] = numberOr(stat.value, null);
    const bonusStat = YAHOO_BONUS_ON_STAT[id];
    if (bonusStat && Array.isArray(stat.bonuses)) {
      for (const b of stat.bonuses) {
        const bonus = b?.bonus || b;
        if (!bonus) continue;
        const threshold = Number(bonus.target);
        const points = Number(bonus.points);
        if (Number.isFinite(threshold) && Number.isFinite(points)) {
          out.bonuses.push({ stat: bonusStat, threshold, points });
        }
      }
    }
  }
  return out;
}

// ---------- scoring display ----------

// Per SCORING_DETAIL_v1: PPR headline + per-yard + per-TD + turnovers, on one
// terse line. Don't dump every K/DEF tier — those go behind the generic
// "Kicker/DEF scoring changed" diff line below.
function describeScoringDetail(scoring) {
  if (!scoring) return null;
  const parts = [];

  const ppr = pprLabel(scoring.rec);
  if (ppr) parts.push(ppr);

  if (scoring.passYd != null || scoring.passTd != null) {
    parts.push(`Pass ${joinScoringSubparts(scoring.passYd, scoring.passTd)}`);
  }

  // Spec example collapses Rush + Rec into one cell when their per-yd and
  // per-TD values match (the common case for non-bonus leagues).
  const rushRecSame =
    scoring.rushYd != null && scoring.rushYd === scoring.recYd &&
    scoring.rushTd != null && scoring.rushTd === scoring.recTd;
  if (rushRecSame) {
    parts.push(`Rush/Rec ${scoring.rushYd}/yd, ${scoring.rushTd}/TD`);
  } else {
    if (scoring.rushYd != null || scoring.rushTd != null) {
      parts.push(`Rush ${joinScoringSubparts(scoring.rushYd, scoring.rushTd)}`);
    }
    if (scoring.recYd != null || scoring.recTd != null) {
      parts.push(`Rec ${joinScoringSubparts(scoring.recYd, scoring.recTd)}`);
    }
  }

  const turnovers = [];
  if (scoring.passInt != null) turnovers.push(`${scoring.passInt}/INT`);
  if (scoring.fumLost != null) turnovers.push(`${scoring.fumLost}/fumble`);
  if (turnovers.length) parts.push(turnovers.join(", "));

  return parts.length ? parts.join(" · ") : null;
}

function joinScoringSubparts(yd, td) {
  const inner = [];
  if (yd != null) inner.push(`${yd}/yd`);
  if (td != null) inner.push(`${td}/TD`);
  return inner.join(", ");
}

function pprLabel(rec) {
  if (rec == null) return null;
  if (rec === 0) return "Standard (0 PPR)";
  if (rec === 0.5) return "Half-PPR";
  if (rec === 1) return "Full PPR";
  return `${rec} PPR`;
}

function describeBonuses(bonuses) {
  if (!bonuses?.length) return null;
  // Group same-threshold-same-points across stats so "rush 150 +5" and
  // "rec 150 +5" collapse to "150 rush/rec yd (+5)".
  const groups = new Map();
  for (const b of bonuses) {
    const k = `${b.threshold}|${b.points}`;
    if (!groups.has(k)) groups.set(k, { threshold: b.threshold, points: b.points, stats: [] });
    groups.get(k).stats.push(bonusStatShort(b.stat));
  }
  return [...groups.values()]
    .map(g => `${g.threshold} ${g.stats.join("/")} yd (+${g.points})`)
    .join(", ");
}

function bonusStatShort(stat) {
  if (stat === "passYd") return "pass";
  if (stat === "rushYd") return "rush";
  if (stat === "recYd") return "rec";
  return stat;
}

// ---------- non-core (K / DEF) scoring fingerprint ----------
//
// Captures every scoring stat OUTSIDE the curated 9 core offensive
// categories as a sorted "key=value" string. YoY diff on the fingerprint
// powers the single-line "Kicker/DEF scoring changed" fallback per spec -
// detects category changes without enumerating every PAT/FG distance tier.

function collectNonCoreScoring(season, platform) {
  if (platform === "sleeper") {
    const scoring = season?.scoring_settings || {};
    const entries = [];
    for (const [k, v] of Object.entries(scoring)) {
      if (SLEEPER_SCORING_KEY[k]) continue;
      entries.push(`${k}=${v}`);
    }
    return entries.sort().join("|");
  }
  if (platform === "yahoo") {
    const stats = season?.settings?.stat_modifiers?.stats || [];
    const entries = [];
    for (const e of stats) {
      const id = Number(e?.stat?.stat_id);
      if (YAHOO_STAT_ID[id]) continue;
      if (!Number.isFinite(id)) continue;
      entries.push(`${id}=${e.stat.value}`);
    }
    return entries.sort().join("|");
  }
  return "";
}

// ---------- year-over-year diff ----------

function diffSeasons(prev, curr) {
  const out = [];

  if (prev.teamCount !== curr.teamCount && prev.teamCount && curr.teamCount) {
    out.push(`Team count ${prev.teamCount} → ${curr.teamCount}`);
  }

  const slotChanges = diffSlots(prev.rosterSlots, curr.rosterSlots);
  out.push(...slotChanges);

  // Scoring diff - per-category for the curated 9 core stats + bonus
  // add/remove diff + a single "Kicker/DEF scoring changed" fallback for
  // anything outside the core (no need to enumerate FG-by-distance tiers
  // or PAT minutiae).
  out.push(...diffScoring(prev.scoring, curr.scoring));
  if (prev.nonCoreScoring !== curr.nonCoreScoring && (prev.nonCoreScoring || curr.nonCoreScoring)) {
    out.push("Kicker / DEF scoring changed");
  }

  if (prev.medianOn !== curr.medianOn) {
    out.push(curr.medianOn ? "Median scoring turned ON" : "Median scoring turned OFF");
  }

  if (prev.playoffTeams !== curr.playoffTeams && prev.playoffTeams && curr.playoffTeams) {
    out.push(`Playoff teams ${prev.playoffTeams} → ${curr.playoffTeams}`);
  }
  if (prev.playoffStartWeek !== curr.playoffStartWeek && prev.playoffStartWeek && curr.playoffStartWeek) {
    out.push(`Playoffs start week ${prev.playoffStartWeek} → ${curr.playoffStartWeek}`);
  }

  // Taxi slots is Sleeper-only; both sides null on Yahoo so the !== guard
  // skips this entire branch.
  if (prev.taxiSlots != null && curr.taxiSlots != null && prev.taxiSlots !== curr.taxiSlots) {
    out.push(`Taxi slots ${prev.taxiSlots} → ${curr.taxiSlots}`);
  }

  return out;
}

// Per-category scoring diff. Emits one line per core stat that changed
// between consecutive seasons, plus add/remove lines for bonuses. PPR
// changes get a named-label diff ("Standard → Half-PPR") when crossing
// the canonical 0/0.5/1 thresholds.
function diffScoring(prev, curr) {
  if (!prev || !curr) return [];
  const out = [];

  if (prev.rec !== curr.rec && (prev.rec != null || curr.rec != null)) {
    out.push(`PPR ${pprLabel(prev.rec) || "—"} → ${pprLabel(curr.rec) || "—"}`);
  }

  const CATS = [
    { key: "passYd",  label: "Pass yd" },
    { key: "passTd",  label: "Pass TD" },
    { key: "passInt", label: "Pass INT" },
    { key: "rushYd",  label: "Rush yd" },
    { key: "rushTd",  label: "Rush TD" },
    { key: "recYd",   label: "Rec yd" },
    { key: "recTd",   label: "Rec TD" },
    { key: "fumLost", label: "Fumble lost" },
  ];
  for (const { key, label } of CATS) {
    if (prev[key] === curr[key]) continue;
    if (prev[key] == null && curr[key] == null) continue;
    out.push(`${label} ${prev[key] ?? "—"} → ${curr[key] ?? "—"}`);
  }

  // Bonuses diff - identity by (stat, threshold, points).
  const bonusKey = (b) => `${b.stat}|${b.threshold}|${b.points}`;
  const prevSet = new Set((prev.bonuses || []).map(bonusKey));
  const currSet = new Set((curr.bonuses || []).map(bonusKey));
  for (const b of curr.bonuses || []) {
    if (!prevSet.has(bonusKey(b))) {
      out.push(`Added ${b.threshold}-yd ${bonusStatShort(b.stat)} bonus (+${b.points})`);
    }
  }
  for (const b of prev.bonuses || []) {
    if (!currSet.has(bonusKey(b))) {
      out.push(`Removed ${b.threshold}-yd ${bonusStatShort(b.stat)} bonus (+${b.points})`);
    }
  }
  return out;
}

function diffSlots(prev, curr) {
  const out = [];
  const allKeys = new Set([...Object.keys(prev), ...Object.keys(curr)]);
  const skip = new Set(["BN", "IR", "TAXI", "RES"]);

  for (const key of allKeys) {
    if (skip.has(key)) continue;
    const prevCount = prev[key] || 0;
    const currCount = curr[key] || 0;
    if (prevCount === currCount) continue;
    if (prevCount === 0 && currCount > 0) {
      // Slot type added — frame as "added X SLOT" for headline clarity.
      const label = SUPER_FLEX_SLOTS.has(key) ? "SUPERFLEX" : key;
      out.push(currCount > 1 ? `Added ${currCount}× ${label}` : `Added ${label} slot`);
    } else if (currCount === 0 && prevCount > 0) {
      const label = SUPER_FLEX_SLOTS.has(key) ? "SUPERFLEX" : key;
      out.push(prevCount > 1 ? `Removed ${prevCount}× ${label}` : `Removed ${label} slot`);
    } else {
      out.push(`${key} slots ${prevCount} → ${currCount}`);
    }
  }
  // Bench size change deserves its own line (separate from starting lineup
  // changes since bench tweaks are usually not format changes per se).
  const benchPrev = prev.BN || 0, benchCurr = curr.BN || 0;
  if (benchPrev && benchCurr && benchPrev !== benchCurr) {
    out.push(`Bench slots ${benchPrev} → ${benchCurr}`);
  }
  return out;
}

// ---------- per-season keeper / draft label ----------

function buildSeasonLabel(season, snap, platform, adaptedDraft) {
  const year = String(season.season);
  const rawDrafts = season.drafts || (season.draft ? [season.draft] : []);
  const rawDraft = rawDrafts[0] || null;

  // Draft type label - the "Snake" / "Auction" mechanic, separate from the
  // keeper/startup/rookie status. Both shown so a viewer can see e.g.
  // "Startup · Auction" or "Keeper · Snake".
  const draftType = rawDraft?.type || adaptedDraft?.draftType || null;
  const draftLabel = draftType && draftType !== "unknown"
    ? draftType.charAt(0).toUpperCase() + draftType.slice(1)
    : null;

  // Kept-player list — BOTH platforms now. Sleeper carries per-pick is_keeper;
  // Yahoo's isKeeper is derived from roster is_keeper.kept (#22-keeper) by
  // yahooToDrafts. Pulled from the adapter-produced stats.drafts (canonical
  // owner names) rather than raw picks (which carry roster_id, not owner).
  let keptPlayers = null;
  const keptPicks = adaptedDraft ? (adaptedDraft.picks || []).filter(p => p?.isKeeper) : [];
  if (keptPicks.length > 0) {
    keptPlayers = keptPicks.map(p => ({
      owner: p.owner || null,
      player: p.player || null,
      position: p.position || null,
      team: p.team || null,
    })).filter(k => k.player);
  }

  let label = "Redraft";
  if (platform === "sleeper") {
    const meta = rawDraft?.metadata?.draft_type;
    const maxKeepers = snap.maxKeepers || rawDraft?.settings?.max_keepers || 0;
    if (meta === "startup") label = "Startup Draft";
    else if (meta === "rookie") label = "Rookie Draft";
    else if (maxKeepers > 0) label = "Keeper League";
  } else if (snap.keeperLeague) {
    label = "Keeper League";
  }
  // Actual kept picks are the strongest signal: a season WITH keepers is a
  // keeper year regardless of the settings flag (these redraft leagues allow
  // ad-hoc keepers and don't always set uses_keeper). Don't override the more
  // specific Startup / Rookie labels.
  if (keptPlayers && keptPlayers.length > 0 && label === "Redraft") {
    label = "Keeper League";
  }

  return { year, label, draftLabel, keptPlayers };
}

// ---------- keeper history (team-centric, #22-keeper) ----------
//
// Walks the adapter-produced drafts (BOTH platforms — picks carry isKeeper +
// canonical owner) and groups every kept pick by owner across all years.
// Powers the standalone "Keepers" section (each team's keepers across years);
// hasKeepers gates whether the section renders at all.
export function collectKeeperHistory(drafts) {
  const byOwner = new Map();
  const yearsWithKeepers = new Set();
  let total = 0;
  for (const d of drafts || []) {
    const year = d?.year != null ? String(d.year) : null;
    for (const p of d?.picks || []) {
      if (!p?.isKeeper) continue;
      const owner = p.owner || "—";
      if (!byOwner.has(owner)) byOwner.set(owner, []);
      byOwner.get(owner).push({
        year,
        player: p.player || "—",
        position: p.position || null,
        team: p.team || null,
        round: p.round ?? null,
        cost: p.cost ?? null,
      });
      if (year) yearsWithKeepers.add(year);
      total++;
    }
  }
  const owners = [...byOwner.entries()]
    .map(([owner, keepers]) => ({
      owner,
      count: keepers.length,
      keepers: keepers.sort((a, b) => String(a.year).localeCompare(String(b.year))),
    }))
    .sort((a, b) => b.count - a.count || a.owner.localeCompare(b.owner));
  return { owners, totalKeepers: total, seasonCount: yearsWithKeepers.size, hasKeepers: total > 0 };
}
